/**
 * A snap in the thread: a small bubble to tap, and the photo or clip once you
 * have.
 *
 * The rules live in ../snap; this is the gesture. Tap somebody else's
 * unopened snap and it fills the screen for ten seconds -- a clip for as long
 * as it runs -- then closes, and is gone for you. Press and hold to stop the
 * clock and look for longer; let go and it runs on. A quick tap closes it
 * early. The look only counts once the photo has loaded, or the clip has
 * started playing -- closing on a spinner is not having seen anything -- so a
 * slow connection does not cost anyone their one look.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { MessageDoc, SnapDecor, UserDoc } from '../../../shared/types';
import { SNAP_LIFETIME_MS, SNAP_REPLAY_MS } from '../../../shared/constants';
import { markSnapViewed, urlFor, type WithId } from '../data';
import { checkSnapShot, openSnapBack, queueSnap, type SnapBackKind } from '../snapBack';
import { SnapCompose, type Shot } from '../sheets/SnapCompose';
import { PhotoBackdrop, SnapDecorLayer, SnapStage, hasDecor, paintBackdrop, ratioOf } from '../SnapDecor';
import { reachesPast } from '../../../shared/snapDecor';
import { filterCss, isSnapFilter } from '../../../shared/snapFilters';
import {
  SNAP_CLOSE_MS,
  SNAP_PULL_CLOSE_PX,
  SNAP_PULL_CLOSE_SPEED,
  SNAP_PULL_SLOP_PX,
  SNAP_TAP_MS,
  SNAP_VIEW_MS,
  SNAP_FACES_SHOWN,
  SNAP_MARKS_SHOWN,
  canReplay,
  isExpired,
  namesLine,
  spentAt,
  isVideoSnap,
  snapStateFor,
  type SnapState,
} from '../snap';
import { avatarOf, setMessageReaction } from '../data';
import { useBackCloses } from '../router';
import { pageZoom } from '../theme';
import { clockTime, dayLabel, toDate, useFollowNewest } from '../hooks';
import { Avatar, IconCamera, IconVideo, ReactionBar, SheetHead, Spinner, emojisOf, useHold } from '../ui';

/** A snap's glyph: a filled square while it can be opened, an outline after. */
function SnapMark({ full }: { full: boolean }) {
  return (
    <span className={full ? 'snap-mark is-full' : 'snap-mark'} aria-hidden="true" />
  );
}

const LINE: Record<Exclude<SnapState, 'yours' | 'gone'>, string> = {
  unopened: 'Tap to view',
  opened: 'Opened',
  expired: 'Expired',
  sending: 'Sending…',
  failed: 'Did not send',
};

/**
 * Starts a clip, with sound if the browser allows it.
 *
 * Called from inside the tap itself, so the play() lands within the user's
 * gesture -- the only moment a browser will ever let sound start. Where it is
 * refused anyway, the clip plays silently rather than not at all, and says so.
 */
function playClip(clip: HTMLVideoElement, onSilenced: () => void): void {
  clip.muted = false;
  const tried = (() => {
    try {
      return Promise.resolve(clip.play());
    } catch (error) {
      return Promise.reject(error);
    }
  })();
  tried.catch(() => {
    clip.muted = true;
    onSilenced();
    void Promise.resolve()
      .then(() => clip.play())
      .catch(() => undefined);
  });
}

/**
 * When a snap was sent, for the corner of the viewer: the time alone today,
 * and the day before it otherwise -- a snap lasts long enough to still be
 * waiting the next morning, when "9:40 PM" would read as tonight.
 */
export function sentLabel(createdAt: unknown): string {
  const time = clockTime(createdAt);
  const date = toDate(createdAt);
  if (!time || !date) return '';
  const day = dayLabel(date);
  return day === 'Today' ? time : `${day} ${time}`;
}

export function SnapBubble({
  message,
  uid,
  users,
  variant = 'pill',
  register,
  onNext,
  position,
}: {
  message: WithId<MessageDoc>;
  uid: string;
  users: Record<string, UserDoc>;
  /**
   * 'pill' inside a sheet, where each snap is a row of its own. 'hidden' for
   * a chip, which draws the snap itself and only needs its viewer.
   */
  variant?: 'pill' | 'hidden';
  /**
   * Inside an exchange: hands the exchange a way to open this snap, and is
   * told when a look ends by moving on -- a tap, or the clock or the clip
   * running out -- so the next snap can open in its place.
   */
  register?: (open: (next?: boolean) => boolean) => void;
  onNext?: () => void;
  /** Its place among the snaps a chip plays: "2 of 3" on the snap itself. */
  position?: { index: number; total: number };
}) {
  const data = message.data;
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  // Spent here, before Firestore says so, so a second tap in the moment
  // between closing and the write coming back cannot open it again.
  const [spent, setSpent] = useState(false);
  // Both: the ref is what finish() reads, from inside a listener made on an
  // earlier render; the state is what takes the spinner away and starts the
  // clock.
  const loaded = useRef(false);
  const [ready, setReady] = useState(false);
  const [silent, setSilent] = useState(false);
  /** How long the look lasts, in ms: ten seconds, or the clip's own length. */
  const [span, setSpan] = useState(SNAP_VIEW_MS);
  /** What is left of it, as of the last time the clock stopped. */
  const left = useRef(SNAP_VIEW_MS);
  /** Whole seconds left, for the chip. Ticked while the clock runs. */
  const [secs, setSecs] = useState(Math.ceil(SNAP_VIEW_MS / 1000));
  const pressedAt = useRef<number | null>(null);
  const release = useRef<(() => void) | null>(null);
  /*
   * The clip is made in the tap rather than rendered after it, so that it can
   * start playing inside the gesture; see playClip. The viewer takes it in
   * once it has drawn.
   */
  const clip = useRef<HTMLVideoElement | null>(null);
  const backdrop = useRef<HTMLCanvasElement>(null);

  const state: SnapState = spent && data.authorUid !== uid ? 'opened' : snapStateFor(data, uid, now);
  const video = isVideoSnap(data);
  /** Looked at already, or yours, and still inside its first day. */
  const replay = (state === 'opened' || state === 'yours') && canReplay(data, now);
  /** Whether this opening is somebody's one look, which is what gets recorded. */
  const firstLook = useRef(false);

  // Stops offering a replay at the end of its day, without a thread change.
  useEffect(() => {
    if (!replay) return;
    const at = (data.createdAt as { toMillis?: () => number } | null)?.toMillis?.();
    if (at === undefined) return;
    const remaining = at + SNAP_REPLAY_MS - Date.now();
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, remaining) + 50);
    return () => window.clearTimeout(timer);
  }, [replay, data.createdAt]);

  /*
   * Turned over to expired when its time is up, even if nothing else about the
   * thread changes -- a snap left on screen at 47 hours would otherwise go on
   * offering itself.
   */
  useEffect(() => {
    if (state !== 'unopened') return;
    const at = (data.createdAt as { toMillis?: () => number } | null)?.toMillis?.();
    if (at === undefined) return;
    const remaining = at + SNAP_LIFETIME_MS - Date.now();
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, remaining) + 50);
    return () => window.clearTimeout(timer);
  }, [state, data.createdAt]);

  /** Lets the browser drop what it had fetched rather than holding on to it. */
  const unload = (playing: HTMLVideoElement | null) => {
    if (!playing) return;
    playing.pause();
    playing.removeAttribute('src');
    playing.load?.();
  };

  /*
   * Closing, drawn: the viewer stays up for SNAP_CLOSE_MS fading out, so it
   * leaves rather than vanishes -- and when an exchange moves on, the next
   * snap fades in over it rather than after a flash of the thread. Everything
   * that matters happens at once; only the picture lingers, and it takes no
   * presses while it goes.
   */
  const [leaving, setLeaving] = useState(false);
  /*
   * How far down a pull it was when it closed, so it zooms out from there
   * rather than jumping back to the middle first.
   */
  const [closedAt, setClosedAt] = useState(0);
  /* Pulled down by a finger, to close it. */
  const [pull, setPull] = useState(0);
  const pullFrom = useRef<{ y: number; at: number } | null>(null);
  const pulling = useRef(false);
  const leaveTimer = useRef<number | undefined>(undefined);
  const leavingClip = useRef<HTMLVideoElement | null>(null);

  const stopClip = () => {
    const playing = clip.current;
    clip.current = null;
    // Stopped now, silent at once; let go of once it is off the screen.
    playing?.pause();
    unload(leavingClip.current);
    leavingClip.current = playing;
  };

  // Nothing left listening, or playing, if the bubble goes while open.
  useEffect(
    () => () => {
      release.current?.();
      window.clearTimeout(leaveTimer.current);
      unload(clip.current);
      unload(leavingClip.current);
    },
    [],
  );

  const finish = (next = false, fromY = 0) => {
    release.current?.();
    release.current = null;
    stopClip();
    setClosedAt(fromY);
    setPull(0);
    pullFrom.current = null;
    pulling.current = false;
    setOpen(false);
    setLeaving(true);
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      setLeaving(false);
      unload(leavingClip.current);
      leavingClip.current = null;
    }, SNAP_CLOSE_MS);
    setPaused(false);
    pressedAt.current = null;
    // Only the first look is recorded; a replay is just another view of it.
    if (loaded.current && firstLook.current) {
      setSpent(true);
      void markSnapViewed(message.id, uid).catch(() => undefined);
    }
    // Moving on, not backing out: the exchange opens whatever comes next.
    if (next) onNextRef.current?.();
  };
  // The clock and the listeners outlive the render that made them.
  const finishRef = useRef(finish);
  finishRef.current = finish;
  useBackCloses(() => finishRef.current(), open);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  /*
   * The photo's clock: runs once it has loaded, stops while held. A clip keeps
   * its own time and closes on 'ended', so it needs none of this -- holding
   * it pauses the clip itself.
   */
  useEffect(() => {
    if (!open || !ready || paused || video) return;
    const started = Date.now();
    const timer = window.setTimeout(() => finishRef.current(true), left.current);
    const tick = window.setInterval(
      () => setSecs(Math.max(0, Math.ceil((left.current - (Date.now() - started)) / 1000))),
      250,
    );
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(tick);
      left.current = Math.max(0, left.current - (Date.now() - started));
    };
  }, [open, ready, paused, video]);

  /** Opens it, and says whether it could: one not openable stays shut. */
  /*
   * Opened by the snap before it moving on, rather than by a tap on this one.
   * Then it is simply there -- no fade in -- so its black covers the screen
   * at once and the one before zooms out unseen beneath it. A fade here let
   * the thread show through for a frame between every snap.
   */
  const [continuing, setContinuing] = useState(false);

  const start = (next = false): boolean => {
    if ((state !== 'unopened' && !replay) || open) return false;
    const media = data.media?.[0];
    if (!media) return false;
    // Its time could have run out since the last render.
    if (state === 'unopened' ? isExpired(data, Date.now()) : !canReplay(data, Date.now())) {
      setNow(Date.now());
      return false;
    }
    firstLook.current = state === 'unopened';
    setContinuing(next);
    loaded.current = false;
    left.current = SNAP_VIEW_MS;
    setSpan(SNAP_VIEW_MS);
    setSecs(Math.ceil(SNAP_VIEW_MS / 1000));
    setReady(false);
    setSilent(false);
    setPaused(false);

    if (video) {
      const made = document.createElement('video');
      made.className = 'snap-photo';
      made.src = urlFor(media);
      made.loop = false;
      made.playsInline = true;
      made.setAttribute('playsinline', '');
      // It counts as seen once it is actually moving, not once it is asked to.
      made.addEventListener('playing', () => {
        loaded.current = true;
        setReady(true);
      });
      made.addEventListener('loadeddata', () => paintBackdrop(backdrop.current, made));
      made.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(made.duration) && made.duration > 0) {
          setSpan(made.duration * 1000);
          setSecs(Math.ceil(made.duration));
        }
      });
      // A clip keeps its own time, so its seconds come from where it is.
      made.addEventListener('timeupdate', () => {
        if (Number.isFinite(made.duration) && made.duration > 0) {
          setSecs(Math.max(0, Math.ceil(made.duration - made.currentTime)));
        }
      });
      made.addEventListener('ended', () => finishRef.current(true));
      clip.current = made;
      playClip(made, () => setSilent(true));
    }
    setOpen(true);

    /*
     * Leaving the app closes it, or a look could be held open indefinitely
     * from the app switcher. Escape, for a keyboard.
     */
    const blur = () => finishRef.current();
    const hidden = () => {
      if (document.visibilityState === 'hidden') finishRef.current();
    };
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') finishRef.current();
    };
    window.addEventListener('blur', blur);
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('keydown', key);
    release.current = () => {
      window.removeEventListener('blur', blur);
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('keydown', key);
    };
    return true;
  };
  const startRef = useRef(start);
  startRef.current = start;

  // Once, at mount: the exchange keeps the opener, which always calls the
  // latest start.
  useEffect(() => {
    register?.((next = false) => startRef.current(next));
  }, []);

  // The row's own hold opens the reaction menu and its drag starts a reply;
  // on an unopened snap the tap is the look, so the row never hears it.
  const onPointerDown = (event: ReactPointerEvent) => {
    if (state !== 'unopened') return;
    event.stopPropagation();
  };

  /* Answering from the snap itself, and seeing who has looked at yours. */
  const [answering, setAnswering] = useState(false);
  const [seeing, setSeeing] = useState(false);
  const viewers = data.viewedBy ?? [];

  const carryOn = () => {
    setPaused(false);
    void clip.current?.play()?.catch(() => undefined);
  };

  /*
   * The viewer keeps its presses to itself. It is drawn over everything but
   * sits inside an exchange's sheet in the tree, so its presses would reach
   * the sheet's own pull-to-close on their way up: pulling a snap down would
   * take the sheet away with it. Stopping the press going down is enough --
   * the sheet only starts a pull from one it heard begin.
   */

  /* Held: the clock stops, and the clip with it. */
  const onViewerDown = (event: ReactPointerEvent) => {
    event.stopPropagation();
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    pressedAt.current = Date.now();
    pullFrom.current = { y: event.clientY, at: Date.now() };
    pulling.current = false;
    setPaused(true);
    clip.current?.pause();
  };

  /* Down far enough and it is a pull, no longer a tap or a hold. */
  const onViewerMove = (event: ReactPointerEvent) => {
    const from = pullFrom.current;
    if (!from) return;
    const down = event.clientY - from.y;
    if (!pulling.current) {
      if (down < SNAP_PULL_SLOP_PX) return;
      pulling.current = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    // Decided in the finger's pixels, drawn in the page's, so it keeps up.
    setPull(Math.max(0, down) / pageZoom());
  };

  /*
   * Let go. From a pull: far enough or fast enough and it closes -- backing
   * right out, not on to the next -- else it springs back and runs on. Else a
   * tap moves on, and the end of a hold starts the clock again.
   */
  const onViewerUp = (event: ReactPointerEvent) => {
    const from = pullFrom.current;
    pullFrom.current = null;
    if (pulling.current && from) {
      pulling.current = false;
      const down = Math.max(0, event.clientY - from.y);
      const speed = down / Math.max(1, Date.now() - from.at);
      if (down > SNAP_PULL_CLOSE_PX || speed > SNAP_PULL_CLOSE_SPEED) {
        finish(false, down / pageZoom());
        return;
      }
      setPull(0);
      carryOn();
      return;
    }
    const at = pressedAt.current;
    pressedAt.current = null;
    if (at === null) return;
    if (Date.now() - at < SNAP_TAP_MS) {
      finish(true);
      return;
    }
    carryOn();
  };

  /* The browser took the press away: nothing was decided, so nothing closes. */
  const onViewerCancel = () => {
    pullFrom.current = null;
    pulling.current = false;
    pressedAt.current = null;
    setPull(0);
    carryOn();
  };

  const media = data.media?.[0];
  const nameOf = (id: string) => users[id]?.displayName ?? 'Someone';
  const title = video ? 'Video snap' : 'Snap';

  const decor: SnapDecor = { caption: data.caption, captionAt: data.captionAt, stickers: data.stickers };
  // A look it was given, from the list only: a name it does not know is no look.
  const look = isSnapFilter(data.filter) ? filterCss(data.filter) : undefined;
  const decorated = hasDecor(decor);
  const picture =
    media &&
    (video ? (
      <span
        className="snap-clip"
        ref={(host) => {
          const made = clip.current;
          if (host && made && made.parentNode !== host) host.appendChild(made);
        }}
      />
    ) : (
      <img
        className="snap-photo"
        src={urlFor(media)}
        alt=""
        draggable={false}
        onLoad={() => {
          loaded.current = true;
          setReady(true);
        }}
      />
    ));

  const viewerOnly =
    (open || leaving) &&
    media &&
    createPortal(
          <div
            className={
              open ? (continuing ? 'snap-viewer is-next' : 'snap-viewer') : 'snap-viewer is-closing'
            }
            style={{
              '--look': look,
              ...(open
                ? pull
                  ? {
                      transform: `translateY(${pull}px) scale(${1 - Math.min(pull, 600) / 2400})`,
                      transition: 'none',
                    }
                  : undefined
                : {
                    '--from-y': `${closedAt}px`,
                    '--from-scale': `${1 - Math.min(closedAt, 600) / 2400}`,
                  }),
            } as unknown as CSSProperties}
            aria-hidden={open ? undefined : true}
            data-filter={look ? data.filter : undefined}
            role="dialog"
            aria-label={`${title} from ${nameOf(data.authorUid)}`}
            onPointerDown={onViewerDown}
            onPointerMove={onViewerMove}
            onPointerUp={onViewerUp}
            onPointerCancel={onViewerCancel}
            onContextMenu={(event) => event.preventDefault()}
          >
            {/* When it was sent, which for a snap is when it was taken. */}
            <span className="snap-when">{sentLabel(data.createdAt)}</span>
            {/*
              * Who sent it, with the time left drawn round their face: one
              * animation the length of the look, paused while held and while
              * it is still loading, and the seconds beside it.
              */}
            <span className="snap-who">
              <span className="snap-ring">
                <svg className="snap-ring-track" viewBox="0 0 48 48" aria-hidden="true">
                  <circle cx="24" cy="24" r="21" />
                  <circle
                    className="snap-ring-fill"
                    cx="24"
                    cy="24"
                    r="21"
                    style={
                      {
                        animationDuration: `${span}ms`,
                        animationPlayState: ready && !paused ? 'running' : 'paused',
                      } as CSSProperties
                    }
                  />
                </svg>
                <Avatar
                  name={nameOf(data.authorUid)}
                  photoURL={avatarOf(users[data.authorUid])}
                  size={36}
                />
              </span>
              <span className="snap-who-text">
                <span className="snap-who-name">{nameOf(data.authorUid)}</span>
                <span className="snap-who-left">
                  {position && position.total > 1 ? `${position.index + 1} of ${position.total} · ` : ''}
                  {paused ? 'Paused' : `${secs}s left`}
                </span>
              </span>
            </span>
            {video ? (
              <canvas ref={backdrop} className="snap-backdrop" aria-hidden="true" />
            ) : (
              <PhotoBackdrop src={urlFor(media)} />
            )}
            {decorated ? (
              // Written on: drawn on a stage the picture's own shape, so
              // everything lands where the sender put it.
              <SnapStage ratio={ratioOf(media.width, media.height)} roomy={reachesPast(decor)}>
                {picture}
                <SnapDecorLayer decor={decor} />
              </SnapStage>
            ) : (
              picture
            )}
            {!ready && (
              <span className="snap-loading">
                <Spinner />
              </span>
            )}
            {/*
              * Answering in kind, from the snap itself: Snapchat's place for
              * it, so a snap back does not wait for a hunt through the
              * thread afterwards. It closes this one -- its look counted --
              * and opens the choice, which stays after the viewer has gone.
              */}
            {data.authorUid !== uid && (
              <button
                type="button"
                className="snap-viewer-back"
                onPointerDown={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
                onClick={() => {
                  // The snap stays up, held still, behind the choice: it is
                  // plainly a reply to this one, not a new snap.
                  setPaused(true);
                  clip.current?.pause();
                  setAnswering(true);
                }}
              >
                Snap back
              </button>
            )}
            {/*
              * Yours: who has looked, and all of them a tap away. The snap
              * holds still while the list is up and runs on after.
              */}
            {data.authorUid === uid && viewers.length > 0 && (
              <SeenFaces
                uids={viewers}
                users={users}
                className="snap-seen snap-viewer-seen"
                onOpen={() => {
                  setPaused(true);
                  clip.current?.pause();
                  setSeeing(true);
                }}
              />
            )}
            <span className="snap-hint">
              {silent ? 'Sound off · ' : ''}
              {paused ? 'Let go to carry on' : 'Hold to pause · tap to close'}
            </span>
          </div>,
      document.body,
    );

  /*
   * Your own, still waiting for its first look: filled, as one waiting for
   * you is. It stayed filled for its whole two days, opened or not, so a snap
   * already opened by half the family looked as if nobody had seen it.
   */
  const unseenYet = (data.viewedBy ?? []).length === 0 && !isExpired(data, now);

  const viewerPortal = (
    <>
      {viewerOnly}
      {answering && (
        <SnapBackSheet
          replyTo={message.id}
          to={nameOf(data.authorUid)}
          overSnap={open}
          onClose={(sent) => {
            setAnswering(false);
            // Sent: this snap is done with, its look counted. Backed out of:
            // back to it, running on from where it was held.
            if (!open) return;
            if (sent) finish();
            else carryOn();
          }}
        />
      )}
      {seeing && (
        <SeenBySheet
          uids={viewers}
          users={users}
          uid={uid}
          onClose={() => {
            setSeeing(false);
            if (open) carryOn();
          }}
        />
      )}
    </>
  );
  const seenFaces = viewers.length > 0 && (
    <SeenFaces uids={viewers} users={users} className="snap-seen" onOpen={() => setSeeing(true)} />
  );

  const kindWord = video ? 'video snap' : 'snap';

  // A chip draws the snap; this only holds its viewer, to be opened from it.
  if (variant === 'hidden') return viewerPortal;

  if (state === 'yours') {
    const inner = (
      <>
        <SnapMark full={unseenYet} />
        <span className="snap-text">
          <span className="snap-title">{title}</span>
          {viewers.length === 0 && <span className="snap-line">Not opened yet</span>}
        </span>
      </>
    );
    // Yours can be looked at again for its first day, as everybody's can.
    if (replay) {
      return (
        <>
          <button
            type="button"
            className="bubble bubble--mine snap-bubble is-openable"
            aria-label={`Your ${kindWord}, tap to view`}
            onClick={() => start()}
            onContextMenu={(event) => event.preventDefault()}
          >
            {inner}
          </button>
          {seenFaces}
          {viewerPortal}
        </>
      );
    }
    return (
      <>
        <span className="bubble bubble--mine snap-bubble">{inner}</span>
        {seenFaces}
        {viewerPortal}
      </>
    );
  }

  const openable = state === 'unopened' || replay;
  const line =
    state === 'gone' ? '' : state === 'opened' && replay ? 'Opened · tap to replay' : LINE[state];
  return (
    <>
      <button
        type="button"
        className={openable ? 'bubble snap-bubble is-openable' : 'bubble snap-bubble'}
        aria-disabled={!openable}
        aria-label={
          openable
            ? `${title} from ${nameOf(data.authorUid)}, tap to view`
            : `${title}, ${line.toLowerCase()}`
        }
        onPointerDown={onPointerDown}
        onClick={openable ? () => start() : undefined}
        onContextMenu={(event) => event.preventDefault()}
      >
        <SnapMark full={state === 'unopened'} />
        <span className="snap-text">
          <span className="snap-title">{title}</span>
          <span className="snap-line">{line}</span>
        </span>
      </button>

      {viewerPortal}
    </>
  );
}

/**
 * A snap and the snap backs to it, as one row.
 *
 * An exchange of snaps between two or three people is a conversation of its
 * own, and the rest of the family has no part in it; drawn a snap at a time it
 * was a run of rows to scroll past. So it is one row -- who is in it, a square
 * for each snap, how many are waiting for you -- and a tap opens the whole
 * exchange in a sheet.
 */
export function SnapConversation({
  snaps,
  uid,
  users,
  onDelete,
}: {
  /** The first snap, then its snap backs, oldest first. */
  snaps: WithId<MessageDoc>[];
  uid: string;
  users: Record<string, UserDoc>;
  onDelete?: (message: WithId<MessageDoc>) => void;
}) {
  const [open, setOpen] = useState(false);
  const now = Date.now();
  const nameOf = (id: string) => (id === uid ? 'You' : (users[id]?.displayName ?? 'Someone'));

  // Everyone in it, in the order they joined, with you last.
  const people = [...new Set(snaps.map((snap) => snap.data.authorUid))].sort(
    (a, b) => Number(a === uid) - Number(b === uid),
  );
  const who = namesLine(people.map(nameOf));
  const waiting = snaps.filter((snap) => snapStateFor(snap.data, uid, now) === 'unopened').length;

  /*
   * Spent: nothing left in it that you can open. It shrinks to a small line
   * then -- on time, while the thread sits open, not on the next visit.
   */
  const over = spentAt(snaps.map((snap) => snap.data), uid);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(over) || over <= Date.now()) return;
    const timer = window.setTimeout(() => tick((n) => n + 1), over - Date.now() + 50);
    return () => window.clearTimeout(timer);
  }, [over]);
  const spent = now >= over;

  // Answering the exchange answers its newest snap from somebody else.
  const answer = [...snaps].reverse().find((snap) => snap.data.authorUid !== uid) ?? snaps[snaps.length - 1];

  return (
    <>
      {spent ? (
        // Still opens the list of what was sent, for its reactions and to
        // delete your own; only the row in the thread has shrunk.
        <button
          type="button"
          className="snap-spent"
          aria-haspopup="dialog"
          aria-label={`Snaps between ${who}, ${snaps.length} snaps`}
          onClick={() => setOpen(true)}
        >
          <SpentMarks count={snaps.length} />
          <span>{who}</span>
        </button>
      ) : (
        <button
          type="button"
          className="snap-convo"
          aria-haspopup="dialog"
          aria-label={`Snaps between ${who}, ${snaps.length} snaps${waiting ? `, ${waiting} new` : ''}`}
          onClick={() => setOpen(true)}
        >
          <span className="snap-convo-faces">
            {people.slice(0, SNAP_FACES_SHOWN).map((person) => (
              <Avatar
                key={person}
                name={users[person]?.displayName ?? '?'}
                photoURL={avatarOf(users[person])}
                size={28}
              />
            ))}
            {people.length > SNAP_FACES_SHOWN && (
              <span className="snap-convo-more-faces">+{people.length - SNAP_FACES_SHOWN}</span>
            )}
          </span>
          <span className="snap-convo-text">
            <span className="snap-convo-who">{who}</span>
            {/* The newest, which is where anything waiting is. */}
            <span className="snap-convo-marks" aria-hidden="true">
              {snaps.slice(-SNAP_MARKS_SHOWN).map((snap) => (
                <SnapMark key={snap.id} full={snapStateFor(snap.data, uid, now) === 'unopened'} />
              ))}
              {snaps.length > SNAP_MARKS_SHOWN && (
                <span className="snap-convo-more">+{snaps.length - SNAP_MARKS_SHOWN}</span>
              )}
            </span>
          </span>
          {waiting > 0 && <span className="snap-convo-new">{waiting} new</span>}
        </button>
      )}

      {open && (
        <SnapListSheet
          snaps={snaps}
          uid={uid}
          users={users}
          onDelete={onDelete}
          title={who}
          label={`Snaps between ${who}`}
          answer={answer}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * A run of snaps listed in a sheet: an exchange's, or one person's from a
 * block of chips. Each opens as it does in the chat, a look moving on to the
 * next; held, each offers its reactions, and Delete on your own.
 */
export function SnapListSheet({
  snaps,
  uid,
  users,
  onDelete,
  title,
  label,
  answer,
  onClose,
}: {
  snaps: WithId<MessageDoc>[];
  uid: string;
  users: Record<string, UserDoc>;
  onDelete?: (message: WithId<MessageDoc>) => void;
  title: string;
  label: string;
  /** What Snap back at the foot answers, or none -- for a list of your own. */
  answer: WithId<MessageDoc> | null;
  onClose: () => void;
}) {
  const swipe = useSwipeDown(onClose);
  useBackCloses(onClose);
  /*
   * One opener per snap, so a look that ends by moving on can open the next
   * one that can be opened. Skipped: one expired, one still being made, one
   * past its day of replays. None left, and the viewer simply stays shut.
   */
  const openers = useRef(new Map<string, (next?: boolean) => boolean>());
  const openAfter = (index: number) => {
    for (const later of snaps.slice(index + 1)) {
      if (openers.current.get(later.id)?.(true)) return;
    }
  };
  /*
   * A list long enough to scroll keeps the vertical swipe for scrolling, and
   * the sheet is pulled down by its top instead; a short one can be pulled
   * down from anywhere.
   */
  const list = useRef<HTMLDivElement | null>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    if (!list.current) return;
    setScrolls(list.current.scrollHeight > list.current.clientHeight);
  }, [snaps.length]);
  // Opens on the newest snap, at the bottom, as the chat does.
  const newest = snaps[snaps.length - 1];
  useFollowNewest(list, newest?.id, newest?.data.authorUid === uid);
  const nameOf = (id: string) => (id === uid ? 'You' : (users[id]?.displayName ?? 'Someone'));

  useEffect(() => {
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });

  return createPortal(
          <div
            className="snap-sheet-scrim"
            onClick={() => !swipe.wasPull() && onClose()}
            {...swipe.area}
          >
            <div
              className="snap-sheet"
              role="dialog"
              aria-label={label}
              onClick={(event) => event.stopPropagation()}
              style={swipe.style}
            >
              <span className="snap-sheet-grip" aria-hidden="true" />
              <SheetHead title={title} onClose={onClose} />
              <div ref={list} className={scrolls ? 'snap-sheet-list is-scrolling' : 'snap-sheet-list'}>
                {/*
                  * Laid out as the chat is: yours on the right, everybody
                  * else's on the left with their face and name, and the time
                  * between them in the middle.
                  */}
                {snaps.map((snap, index) => {
                  const mine = snap.data.authorUid === uid;
                  const author = users[snap.data.authorUid];
                  const body = snap.data.deletedAt ? (
                    <span className="bubble bubble--gone">Snap deleted</span>
                  ) : (
                    <SnapBubble
                      message={snap}
                      uid={uid}
                      users={users}
                      register={(opener) => openers.current.set(snap.id, opener)}
                      onNext={() => openAfter(index)}
                    />
                  );
                  return (
                    <Fragment key={snap.id}>
                      <span className="snap-sheet-time">{clockTime(snap.data.createdAt)}</span>
                      {mine ? (
                        <div className="snap-sheet-row is-mine">
                          <HeldSnap snap={snap} uid={uid} mine onDelete={onDelete}>
                            {body}
                          </HeldSnap>
                        </div>
                      ) : (
                        <div className="snap-sheet-row">
                          <Avatar
                            name={author?.displayName ?? '?'}
                            photoURL={avatarOf(author)}
                            size={28}
                          />
                          <span className="snap-sheet-theirs">
                            <span className="snap-sheet-who">{nameOf(snap.data.authorUid)}</span>
                            <HeldSnap snap={snap} uid={uid} mine={false} onDelete={onDelete}>
                              {body}
                            </HeldSnap>
                          </span>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
              {answer && (
                <SnapBackButton
                  replyTo={answer.id}
                  to={nameOf(answer.data.authorUid)}
                  className="btn btn--block snap-sheet-back"
                />
              )}
            </div>
          </div>,
          document.body,
        );
}

/**
 * Snap back: a photo or a video, each straight into the phone's camera.
 *
 * Two buttons because Android will not open its camera for both at once (see
 * ../snapBack). Each opens the camera from inside its own tap, which is the
 * only moment a browser allows it. The sheet stays open behind the camera, and
 * what comes back is held up in it to be written on before it goes.
 */
export function SnapBackButton({
  replyTo,
  to,
  className,
}: {
  replyTo: string;
  /** Who it answers, for the sheet's title. */
  to: string;
  className: string;
}) {
  const [choosing, setChoosing] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className}
        aria-haspopup="dialog"
        // Kept off the row's own press, so tapping it is only ever this.
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => setChoosing(true)}
      >
        Snap back
      </button>
      {choosing && <SnapBackSheet replyTo={replyTo} to={to} onClose={() => setChoosing(false)} />}
    </>
  );
}

/**
 * Photo or video for a snap back, then the shot to write on and send.
 *
 * Its own piece so that a Snap back from the snap itself -- whose viewer goes
 * as it closes -- can open it from outside the viewer, where it can stay.
 */
export function SnapBackSheet({
  replyTo,
  to,
  onClose,
  overSnap = false,
}: {
  replyTo: string;
  to: string;
  /** Told whether a snap back went, or the sheet was only backed out of. */
  onClose: (sent: boolean) => void;
  /** Opened from the snap itself, so drawn over it, the snap still behind. */
  overSnap?: boolean;
}) {
  const [held, setHeld] = useState<(Shot & { kind: SnapBackKind }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = (sent = false) => {
    if (held) URL.revokeObjectURL(held.preview);
    setHeld(null);
    setError(null);
    onClose(sent);
  };
  const swipe = useSwipeDown(() => close());
  useBackCloses(() => close());

  useEffect(() => {
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });

  const take = (kind: SnapBackKind) => {
    if (held) URL.revokeObjectURL(held.preview);
    setHeld(null);
    openSnapBack(kind, (file) => {
      void checkSnapShot(file, setError).then((ok) => {
        if (!ok || !file) return;
        setError(null);
        setHeld({ file, preview: URL.createObjectURL(file), video: ok.video, kind });
      });
    });
  };

  const send = (decor: SnapDecor) => {
    if (!held) return;
    queueSnap(held.file, held.video, { replyTo, decor });
    close(true);
  };

  const discard = () => {
    if (!held) return;
    URL.revokeObjectURL(held.preview);
    setHeld(null);
  };

  return createPortal(
    <div
      className={overSnap ? 'snap-choose-scrim is-over-snap' : 'snap-choose-scrim'}
      onClick={() => !swipe.wasPull() && close()}
      {...swipe.area}
    >
      <div
        className="snap-choose"
        role="dialog"
        aria-label={`Snap back to ${to}`}
        onClick={(event) => event.stopPropagation()}
        style={swipe.style}
      >
        <span className="snap-sheet-grip" aria-hidden="true" />
        <span className="snap-choose-title">Snap back to {to}</span>
        {/* Full screen over the sheet, which waits underneath with its choices. */}
        {held && (
          <SnapCompose
            key={held.preview}
            shot={held}
            to={to}
            onRetake={() => take(held.kind)}
            onDiscard={discard}
            onSend={send}
          />
        )}
        <div className="snap-choose-kinds">
          <button type="button" className="snap-choose-kind" onClick={() => take('photo')}>
            <IconCamera size={30} />
            Photo
          </button>
          <button type="button" className="snap-choose-kind" onClick={() => take('video')}>
            <IconVideo size={30} />
            Video
          </button>
        </div>
        {error && <span className="snap-choose-error">{error}</span>}
        <button type="button" className="snap-sheet-close" onClick={() => close()}>
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** Past this far down, or this fast, letting go closes the sheet. */
const SWIPE_CLOSE_PX = 90;
const SWIPE_CLOSE_SPEED = 0.6;
/** Less than this is a tap, which belongs to whatever was under the finger. */
const SWIPE_SLOP_PX = 8;

/**
 * Pull a sheet down to close it, as a phone's own sheets close.
 *
 * It follows the finger, and goes if let go far enough down or flicked; short
 * of that it springs back. A tap is never taken for a pull, so the buttons on
 * the sheet still work.
 *
 * `area` goes on the whole screen behind the sheet, so a pull can start
 * anywhere -- on the sheet, whose presses reach it on their way up, or on the
 * dimmed thread above -- and `style` on the sheet, which is what moves.
 */
export function useSwipeDown(onClose: () => void) {
  const [drag, setDrag] = useState(0);
  const from = useRef<{ y: number; at: number } | null>(null);
  const pulling = useRef(false);
  // A pull let go short of closing still ends in a click on the backdrop,
  // which would close it anyway; this is how that click is told apart. Set at
  // every let-go, so it always speaks for the gesture just finished.
  const lastWasPull = useRef(false);

  const reset = () => {
    from.current = null;
    pulling.current = false;
    setDrag(0);
  };

  const area = {
    onPointerDown: (event: ReactPointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      from.current = { y: event.clientY, at: Date.now() };
      pulling.current = false;
    },
    onPointerMove: (event: ReactPointerEvent) => {
      const start = from.current;
      if (!start) return;
      const down = event.clientY - start.y;
      if (!pulling.current) {
        if (down < SWIPE_SLOP_PX) return;
        pulling.current = true;
        // Held from here, so a pull that leaves the sheet is still this one.
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      // Drawn in the page's own units, which the text size zooms; how far
      // counts as closing, below, is still the finger's own distance.
      setDrag(Math.max(0, down) / pageZoom());
    },
    onPointerUp: (event: ReactPointerEvent) => {
      const start = from.current;
      const pulled = pulling.current;
      reset();
      lastWasPull.current = pulled;
      if (!start || !pulled) return;
      const down = event.clientY - start.y;
      const speed = down / Math.max(1, Date.now() - start.at);
      if (down > SWIPE_CLOSE_PX || speed > SWIPE_CLOSE_SPEED) onClose();
    },
    onPointerCancel: reset,
  };

  return {
    style: drag ? ({ transform: `translateY(${drag}px)`, transition: 'none' } as CSSProperties) : undefined,
    area,
    /** Whether the gesture that just ended was a pull rather than a tap. */
    wasPull: () => lastWasPull.current,
  };
}

/** The same hold as a chat message: long enough not to be a tap. */
const SNAP_HOLD_MS = 420;
/** Further than this and the finger is pulling the sheet, not holding. */
const SNAP_HOLD_SLOP_PX = 8;

/** Each emoji and how many chose it, in the order they were first chosen. */
function tallyOf(likes: MessageDoc['likes'] | undefined): { emoji: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const value of Object.values(likes ?? {})) {
    for (const emoji of emojisOf(value)) counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
  }
  return [...counts].map(([emoji, count]) => ({ emoji, count }));
}

/**
 * A snap in an exchange's sheet, held the way a chat message is: press and
 * hold to react, and on your own to delete it. A tap still opens it.
 *
 * The hold is listened for on the way down, before the snap itself hears the
 * press -- an unopened snap keeps its press to itself -- and the click that
 * ends a hold is swallowed on the way down too, so holding never also opens
 * it. The menu opens under the snap rather than over it, where the top of the
 * sheet's list would cut it off.
 */
function HeldSnap({
  snap,
  uid,
  mine,
  onDelete,
  children,
}: {
  snap: WithId<MessageDoc>;
  uid: string;
  mine: boolean;
  onDelete?: (message: WithId<MessageDoc>) => void;
  children: ReactNode;
}) {
  const [menu, setMenu] = useState(false);
  const hold = useHold(() => setMenu(true), SNAP_HOLD_MS);
  const box = useRef<HTMLDivElement | null>(null);
  const downAt = useRef<{ x: number; y: number } | null>(null);

  // Away from the menu closes it, as in the chat.
  useEffect(() => {
    if (!menu) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setMenu(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [menu]);

  const my = emojisOf(snap.data.likes?.[uid]);
  const react = (emoji: string) => {
    const next = my.includes(emoji) ? my.filter((each) => each !== emoji) : [...my, emoji];
    void setMessageReaction(snap.id, uid, next).catch(() => undefined);
    // Picking one is the whole errand, as it is in the chat.
    setMenu(false);
  };
  const tally = tallyOf(snap.data.likes);

  return (
    <div
      ref={box}
      className={mine ? 'snap-held is-mine' : 'snap-held'}
      onPointerDownCapture={(event) => {
        /*
         * A press on the menu or a chip is a choice, not another hold, and it
         * clears the hold before it. A phone that sends no click when a long
         * press lifts would otherwise leave that one waiting to be swallowed,
         * and the first emoji tapped would do nothing.
         */
        if ((event.target as Element).closest('.snap-hold-menu, .reaction-row')) {
          hold.consumed();
          return;
        }
        downAt.current = { x: event.clientX, y: event.clientY };
        hold.handlers.onPointerDown(event as ReactPointerEvent<HTMLElement>);
      }}
      onPointerMoveCapture={(event) => {
        const at = downAt.current;
        if (!at) return;
        if (Math.hypot(event.clientX - at.x, event.clientY - at.y) > SNAP_HOLD_SLOP_PX) {
          downAt.current = null;
          hold.handlers.onPointerCancel();
        }
      }}
      onPointerUpCapture={() => {
        downAt.current = null;
        hold.handlers.onPointerUp();
      }}
      onPointerCancelCapture={hold.handlers.onPointerCancel}
      onContextMenu={hold.handlers.onContextMenu}
      onClickCapture={(event) => {
        if (!hold.consumed()) return;
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {children}
      {tally.length > 0 && (
        <span className="reaction-row">
          {tally.map(({ emoji, count }) => (
            <button
              key={emoji}
              type="button"
              className={my.includes(emoji) ? 'reaction-chip is-mine' : 'reaction-chip'}
              aria-label={my.includes(emoji) ? `Remove your ${emoji}` : `React ${emoji}`}
              onClick={() => react(emoji)}
            >
              <span>{emoji}</span>
              {count > 1 && <span className="tiny bold">{count}</span>}
            </button>
          ))}
        </span>
      )}
      {menu && (
        <div className="snap-hold-menu">
          <ReactionBar mine={my} onPick={react} />
          {mine && !snap.data.deletedAt && onDelete && (
            <span className="message-actions">
              <button
                type="button"
                className="is-danger"
                onClick={() => {
                  setMenu(false);
                  onDelete(snap);
                }}
              >
                Delete
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A spent row's squares: small outlines, one a snap, up to SNAP_MARKS_SHOWN
 * and then how many more, so the whole family in one exchange is still one
 * short line.
 */
function SpentMarks({ count }: { count: number }) {
  return (
    <span className="snap-spent-marks" aria-hidden="true">
      {Array.from({ length: Math.min(count, SNAP_MARKS_SHOWN) }, (_, i) => (
        <span key={i} className="snap-spent-mark" />
      ))}
      {count > SNAP_MARKS_SHOWN && <span>+{count - SNAP_MARKS_SHOWN}</span>}
    </span>
  );
}

/** How many faces "Seen by" shows before it says how many more. */
const SEEN_FACES_SHOWN = 5;

/**
 * Who has looked at your snap, as faces -- the first few, then "+N" -- and a
 * button for all of them. Faces rather than names, so the whole family fits.
 */
function SeenFaces({
  uids,
  users,
  className,
  onOpen,
}: {
  uids: string[];
  users: Record<string, UserDoc>;
  className: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={className}
      aria-label={`Seen by ${uids.length}`}
      aria-haspopup="dialog"
      // Its own, not the row's, nor the viewer's.
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onClick={onOpen}
    >
      <span className="snap-seen-label">Seen by</span>
      <span className="snap-seen-faces" aria-hidden="true">
        {uids.slice(0, SEEN_FACES_SHOWN).map((id) => (
          <Avatar
            key={id}
            name={users[id]?.displayName ?? '?'}
            photoURL={avatarOf(users[id])}
            size={20}
          />
        ))}
      </span>
      {uids.length > SEEN_FACES_SHOWN && (
        <span className="snap-seen-more">+{uids.length - SEEN_FACES_SHOWN}</span>
      )}
    </button>
  );
}

/**
 * Everyone who has looked at your snap, face and name, in the order they
 * looked, and how many have not yet. Over the snap when opened from it.
 */
function SeenBySheet({
  uids,
  users,
  uid,
  onClose,
}: {
  uids: string[];
  users: Record<string, UserDoc>;
  uid: string;
  onClose: () => void;
}) {
  const swipe = useSwipeDown(onClose);
  useBackCloses(onClose);
  useEffect(() => {
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });
  // Everyone it could have reached who has not looked: the family, less you.
  const notYet = Object.keys(users).filter((id) => id !== uid && !uids.includes(id)).length;

  return createPortal(
    <div
      className="snap-sheet-scrim snap-seen-scrim"
      onClick={() => !swipe.wasPull() && onClose()}
      {...swipe.area}
    >
      <div
        className="snap-sheet"
        role="dialog"
        aria-label={`Seen by ${uids.length}`}
        onClick={(event) => event.stopPropagation()}
        style={swipe.style}
      >
        <span className="snap-sheet-grip" aria-hidden="true" />
        <SheetHead title="Seen by" count={uids.length} onClose={onClose}>
          {notYet > 0 && <span className="snap-seen-notyet">{notYet} haven&rsquo;t yet</span>}
        </SheetHead>
        <div className="snap-sheet-list">
          {uids.map((id) => (
            <div key={id} className="snap-seen-row">
              <Avatar
                name={users[id]?.displayName ?? '?'}
                photoURL={avatarOf(users[id])}
                size={32}
              />
              <span className="snap-seen-name">{users[id]?.displayName ?? 'Someone'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * A block of snaps on their own, as chips: one a person, with how many they
 * sent, in the order of each person's latest -- whoever sent last is last.
 *
 * A tap plays that person's snaps in the order sent, a look moving on to the
 * next; held, a chip lists them, for reactions and to delete your own, since
 * a chip of several cannot say which one a menu is for.
 */
export function SnapChips({
  snaps,
  uid,
  users,
  onDelete,
}: {
  /** The block, in the order sent. */
  snaps: WithId<MessageDoc>[];
  uid: string;
  users: Record<string, UserDoc>;
  onDelete?: (message: WithId<MessageDoc>) => void;
}) {
  const now = Date.now();
  const openers = useRef(new Map<string, (next?: boolean) => boolean>());
  const [listing, setListing] = useState<string | null>(null);

  // One a person, each in the order sent; the people in order of their latest.
  const byPerson = new Map<string, WithId<MessageDoc>[]>();
  snaps.forEach((snap) => {
    const theirs = byPerson.get(snap.data.authorUid) ?? [];
    theirs.push(snap);
    byPerson.set(snap.data.authorUid, theirs);
  });
  const latestAt = (person: string) =>
    Math.max(...(byPerson.get(person) ?? []).map((snap) => snaps.indexOf(snap)));
  const people = [...byPerson.keys()].sort((a, b) => latestAt(a) - latestAt(b));
  const nameOf = (id: string) => (id === uid ? 'You' : (users[id]?.displayName ?? 'Someone'));

  /*
   * A chip turns dim when nothing of that person's is left to open: on time,
   * while the thread sits open, as spent rows do.
   */
  const ends = people.map((person) => spentAt((byPerson.get(person) ?? []).map((snap) => snap.data), uid));
  const nextEnd = Math.min(...ends.filter((at) => Number.isFinite(at) && at > now));
  const [, tick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(nextEnd)) return;
    const timer = window.setTimeout(() => tick((n) => n + 1), nextEnd - Date.now() + 50);
    return () => window.clearTimeout(timer);
  }, [nextEnd]);

  /** Opens the first of `theirs` from `from` that can be opened. */
  const playFrom = (theirs: WithId<MessageDoc>[], from: number, next: boolean) => {
    for (const snap of theirs.slice(from)) {
      if (openers.current.get(snap.id)?.(next)) return;
    }
  };

  const play = (person: string) => {
    const theirs = byPerson.get(person) ?? [];
    // From the first still waiting, if any; else from the start, as replays.
    const waiting = theirs.findIndex((snap) => snapStateFor(snap.data, uid, Date.now()) === 'unopened');
    playFrom(theirs, Math.max(0, waiting), false);
  };

  const listed = listing ? (byPerson.get(listing) ?? []) : [];

  return (
    <div className="snap-chips">
      {people.map((person, i) => {
        const theirs = byPerson.get(person) ?? [];
        const waiting = theirs.filter((snap) => snapStateFor(snap.data, uid, now) === 'unopened').length;
        return (
          <SnapChip
            key={person}
            name={nameOf(person)}
            user={users[person]}
            mine={person === uid}
            count={theirs.length}
            waiting={waiting}
            spent={now >= ends[i]}
            onTap={() => play(person)}
            onHold={() => setListing(person)}
          />
        );
      })}

      {/* Each snap's own viewer, opened from its person's chip. */}
      {people.map((person) => {
        const theirs = byPerson.get(person) ?? [];
        return theirs.map((snap, index) => (
          <SnapBubble
            key={snap.id}
            message={snap}
            uid={uid}
            users={users}
            variant="hidden"
            position={{ index, total: theirs.length }}
            register={(opener) => openers.current.set(snap.id, opener)}
            onNext={() => playFrom(theirs, index + 1, true)}
          />
        ));
      })}

      {listing && (
        <SnapListSheet
          snaps={listed}
          uid={uid}
          users={users}
          onDelete={onDelete}
          title={nameOf(listing)}
          label={listing === uid ? 'Your snaps' : `Snaps from ${nameOf(listing)}`}
          answer={listing === uid ? null : (listed[listed.length - 1] ?? null)}
          onClose={() => setListing(null)}
        />
      )}
    </div>
  );
}

/** One person's chip in a block: a tap plays, a hold lists. */
function SnapChip({
  name,
  user,
  mine,
  count,
  waiting,
  spent,
  onTap,
  onHold,
}: {
  name: string;
  user: UserDoc | undefined;
  mine: boolean;
  count: number;
  waiting: number;
  spent: boolean;
  onTap: () => void;
  onHold: () => void;
}) {
  const hold = useHold(() => onHold(), 420);
  const classes = ['snap-chip', waiting > 0 ? 'is-waiting' : '', spent ? 'is-spent' : '', mine ? 'is-mine' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={classes}
      aria-label={`${name}, ${count} ${count === 1 ? 'snap' : 'snaps'}${waiting ? `, ${waiting} new` : ''}`}
      {...hold.handlers}
      onClick={() => {
        if (hold.consumed()) return;
        onTap();
      }}
    >
      <Avatar name={user?.displayName ?? '?'} photoURL={avatarOf(user)} size={22} />
      <span className="snap-chip-name">{name}</span>
      {count > 1 && <span className="snap-chip-count">{count}</span>}
      {count === 1 && waiting > 0 && <span className="snap-chip-dot" aria-hidden="true" />}
    </button>
  );
}
